import algoliasearch from "algoliasearch/lite";
(function () {
  "use strict";

  // Get defaults from CampsUtils
  const defaults = window.CampsUtils?.defaults || {};

  const CampsNearYouSport = {
    // Configuration (uses CampsUtils.defaults)
    config: {
      displayCount: defaults.displayCount || 8,
      /** Fetch pool size from Algolia to find enough camps with sessions */
      fetchPoolSize: (defaults.displayCount || 8) * 5,
      algoliaAppId: defaults.algoliaAppId,
      algoliaSearchKey: defaults.algoliaSearchKey,
      algoliaIndex: defaults.algoliaIndex,
      cacheKey: defaults.cacheKey,
      cacheDuration: defaults.cacheDuration,
      nearbyRadius: 250 * 1609, // 250 miles in meters
    },

    // DOM elements
    elements: {
      container: null,
      list: null,
      title: null,
      description: null,
      titleText: null,
      titleNear: null,
      cityZipPrefix: null,
      cityZip: null,
      viewAllLink: null,
    },

    // Sport slug from data attribute
    sportSlug: null,

    // Camp codes from data attribute (curated selection from Craft)
    campCodes: [],

    // Algolia index
    index: null,

    /**
     * Initialize the component
     */
    init: function () {
      this.elements.container = document.querySelector(
        '.rd-camps-near-you[data-page="sport"]',
      );
      if (!this.elements.container) return;

      // Get sportSlug from data attribute
      this.sportSlug = this.elements.container.dataset.sportSlug;
      if (!this.sportSlug) {
        console.error(
          "[CampsNearYouSport] ERROR: sportSlug not found in data attribute",
        );
        return;
      }

      // Get curated camp codes from data attribute
      try {
        this.campCodes = JSON.parse(
          this.elements.container.dataset.campCodes || "[]",
        );
      } catch (e) {
        this.campCodes = [];
      }

      this.elements.list = this.elements.container.querySelector(
        ".rd-camps-near-you__list",
      );
      this.elements.title = this.elements.container.querySelector(
        ".rd-camps-near-you__title",
      );
      this.elements.description = this.elements.container.querySelector(
        ".rd-camps-near-you__description",
      );
      this.elements.titleText =
        this.elements.container.querySelector("[data-title-text]");
      this.elements.titleNear =
        this.elements.container.querySelector("[data-title-near]");
      this.elements.cityZipPrefix = this.elements.container.querySelector(
        "[data-city-zip-prefix]",
      );
      this.elements.cityZip =
        this.elements.container.querySelector("[data-city-zip]");
      this.elements.viewAllLink = this.elements.container.querySelector(
        ".rd-camps-near-you__view-all",
      );

      console.log(
        `[CampsNearYouSport] FLOW: Initialized for sport: ${this.sportSlug} with ${this.campCodes.length} curated camps`,
      );

      this.observeAndLoad();
    },

    /**
     * Defer Algolia request until the section enters the viewport.
     * Saves Algolia operations for users who never scroll to this block.
     */
    observeAndLoad: function () {
      if (!("IntersectionObserver" in window)) {
        this.waitForAlgoliaAndLoad();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observer.disconnect();
              this.waitForAlgoliaAndLoad();
              break;
            }
          }
        },
        { rootMargin: "200px 0px", threshold: 0 },
      );

      observer.observe(this.elements.container);
    },

    /**
     * Wait for Algolia library to load, then initialize
     */
    waitForAlgoliaAndLoad: function () {
      const maxAttempts = 50; // 5 seconds max
      let attempts = 0;

      const checkAlgolia = () => {
        if (typeof algoliasearch !== "undefined") {
          const client = algoliasearch(
            this.config.algoliaAppId,
            this.config.algoliaSearchKey,
          );
          this.index = client.initIndex(this.config.algoliaIndex);
          this.loadCamps();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkAlgolia, 100);
        } else {
          console.error(
            "[CampsNearYouSport] ERROR: Algolia library failed to load after 5 seconds",
          );
        }
      };

      checkAlgolia();
    },

    skeletonToggle: function (show) {
      if (this.elements.title) {
        this.elements.title.classList.toggle("rd-skeleton", show);
      }
      if (this.elements.description) {
        this.elements.description.classList.toggle("rd-skeleton", show);
      }
    },

    /**
     * Load camps into the block.
     *
     * Skeletons are shown in the template until this function replaces them
     * with real cards from Algolia.
     *
     * Either/or by design (per the "Camps near you UPD" spec): we show EITHER the
     * nearby Algolia camps OR the curated Craft camps, never a mix of both.
     *
     * Flow:
     *   1. IP lookup (no Algolia).
     *   2. US → geo request with NO radius limit; Algolia returns all sport camps
     *      sorted by increasing distance. 250mi is only a GATE (are any nearby?),
     *      not a display filter.
     *      - ≥1 camp within 250mi → render the nearest Algolia camps (up to 8),
     *        sorted by distance, with distance shown. No curated Craft mix.
     *      - 0 within 250mi → curated-by-codes only, render those (no distance).
     *   3. Non-US → curated-by-codes only, render those (no distance).
     *
     * The geo attempt is isolated in its own try/catch so a transient Algolia
     * failure degrades to the curated fallback instead of blanking the block,
     * and the list never stays on raw skeletons.
     */
    loadCamps: async function () {
      let rendered = false;
      try {
        const location = await window.CampsUtils?.getUserLocationByIP({
          cacheKey: this.config.cacheKey,
          cacheDuration: this.config.cacheDuration,
        });

        // US near-you geo attempt. Isolated try/catch: a transient Algolia
        // failure here must fall through to the curated fallback below — NOT
        // abort the whole block. (The old code shared one try with curated, so
        // a geo throw jumped straight to catch and skipped curated, leaving
        // skeletons forever.)
        if (location?.isUS) {
          console.log(
            "[CampsNearYouSport] FLOW: US user → fetching sport camps (sorted by distance) from Algolia",
          );
          try {
            // No radius limit: Algolia returns all sport camps sorted by distance.
            const hits = await this.fetchCampsBySport(location);

            // GATE (not a display filter): is at least one camp within 250mi?
            // ≥1 → show the nearest camps sorted by distance (may extend past
            // 250mi for sparse sports, per spec). 0 → curated fallback below.
            const hasNearCamps = hits.some(
              (c) =>
                (c._rankingInfo?.geoDistance ?? Infinity) <=
                this.config.nearbyRadius,
            );

            if (hasNearCamps) {
              this.updateTitleForUS(location);
              this.updateViewAllLink(location);
              // Nearest pick (featured-pinned → sessions-priority → raw slice),
              // sorted by increasing distance. Fallback cascade so a missing
              // helper never yields an empty list. Either/or: render ONLY these
              // Algolia camps — no curated Craft mix.
              const campsToShow =
                window.CampsUtils?.selectWithFeaturedPinned?.(
                  hits,
                  this.config.displayCount,
                  window.CampsUtils?.defaults?.featuredConfig?.maxPinnedNearYou,
                  "camps-near-you-sport",
                  this.sportSlug,
                ) ||
                window.CampsUtils?.selectCampsWithSessionsPriority?.(
                  hits,
                  this.config.displayCount,
                ) ||
                hits.slice(0, this.config.displayCount);

              if (
                campsToShow.length > 0 &&
                this.renderCamps(campsToShow, true)
              ) {
                rendered = true;
                return;
              }
            }

            console.log(
              "[CampsNearYouSport] FLOW: No camps within 250 miles → hydrating curated",
            );
          } catch (geoErr) {
            console.warn(
              "[CampsNearYouSport] geo request failed, falling back to curated:",
              geoErr,
            );
          }
        } else {
          console.log(
            "[CampsNearYouSport] FLOW: Non-US user → hydrating curated",
          );
        }

        // Curated fallback: non-US, no nearby camps, OR the geo request failed.
        if (this.campCodes.length > 0) {
          try {
            const curatedCamps = await window.CampsUtils?.fetchCampsByCodes(
              this.index,
              this.campCodes,
            );
            if (
              curatedCamps &&
              curatedCamps.length > 0 &&
              this.renderCamps(curatedCamps, false)
            ) {
              rendered = true;
            }
          } catch (curErr) {
            console.error("[CampsNearYouSport] curated fetch failed:", curErr);
          }
        }
      } catch (error) {
        console.error("[CampsNearYouSport] ERROR:", error);
      } finally {
        // Never leave raw skeletons sitting in the list. If nothing rendered
        // (e.g. a total Algolia outage), show the no-results state instead.
        if (!rendered) this.handleNoCamps();
        this.skeletonToggle(false);
      }
    },

    /**
     * Fetch camps for this sport sorted by distance from the user, with NO
     * radius limit — the block shows the nearest camps in increasing-distance
     * order (per the "Camps near you UPD" spec). The 250mi radius is only a
     * client-side GATE in loadCamps (are there any nearby?) to choose near-you
     * vs the curated admin fallback; it does NOT cap what's displayed.
     * @param {Object} coords - User coordinates
     */
    fetchCampsBySport: async function (coords) {
      const { hits } = await this.index.search("", {
        aroundLatLng: `${coords.lat},${coords.lng}`,
        // No aroundRadius — Algolia returns all sport camps sorted by distance.
        hitsPerPage: this.config.fetchPoolSize,
        filters: `sportSlug:"${this.sportSlug}"`,
        getRankingInfo: true,
      });
      return hits;
    },

    /**
     * Update title for US users with nearby camps
     * Changes from "POPULAR BASKETBALL CAMPS IN THE UNITED STATES"
     * to "Basketball Camps near [City, ST]"
     * @param {Object} location - User location data
     */
    updateTitleForUS: function (location) {
      const locationText =
        location.userLocation || `${location.city}, ${location.regionCode}`;

      // Hide the default title text
      if (this.elements.titleText) {
        this.elements.titleText.classList.add("hidden");
      }

      // Show the "near" title and location elements
      if (this.elements.titleNear) {
        this.elements.titleNear.classList.remove("hidden");
      }

      if (this.elements.cityZipPrefix) {
        this.elements.cityZipPrefix.textContent = "near";
        this.elements.cityZipPrefix.classList.remove("hidden");
      }

      if (this.elements.cityZip) {
        this.elements.cityZip.textContent = locationText;
        this.elements.cityZip.href = `/search/results?q=${encodeURIComponent(
          locationText,
        )}&sportSlug=${this.sportSlug}`;
        this.elements.cityZip.classList.remove("hidden");
      }
    },

    /**
     * Render camp cards to DOM
     * @param {Array} camps - Array of camp hits from Algolia
     * @param {boolean} showDistanceFlag - Whether distance should be shown
     */
    renderCamps: function (camps, showDistanceFlag) {
      if (!this.elements.list) return false;

      if (camps.length === 0) {
        this.handleNoCamps();
        return false;
      }

      const html = camps
        .map((camp) => {
          // Show distance whenever Algolia returned a number (incl. 0.0 mi for a
          // camp at the exact center); hide only when geo data is absent.
          const showDistance =
            showDistanceFlag &&
            typeof camp._rankingInfo?.geoDistance === "number";
          const showSessions = true;
          return window.CampsUtils?.renderCampCardHtml(camp, {
            showDistance,
            showSessions,
          });
        })
        .join("");

      this.elements.list.innerHTML = html;

      // Analytics must never break the block: a throw here used to bubble up
      // and abort rendering even though cards were already in the DOM.
      try {
        pushGTMViewItemList(camps, false, null);
        selectItemTracking(camps, null);
      } catch (e) {
        console.warn("[CampsNearYouSport] tracking failed (non-fatal):", e);
      }

      return true;
    },

    /**
     * Handle case when no camps found for this sport
     */
    handleNoCamps: function () {
      if (this.elements.list) {
        this.elements.list.innerHTML =
          '<li class="rd-camps-near-you__no-results">No camps found for this sport.</li>';
      }
    },

    /**
     * Update "View All" link with location parameter for US users
     * @param {Object} coords - User coordinates and location data
     */
    updateViewAllLink: function (coords) {
      if (!this.elements.viewAllLink) return;

      const locationParam =
        coords.userLocation || `${coords.city}, ${coords.regionCode}`;
      const params = new URLSearchParams();
      params.set("q", locationParam);
      params.set("sportSlug", this.sportSlug);

      this.elements.viewAllLink.href = `/search/results?${params.toString()}`;
    },
  };

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      CampsNearYouSport.init(),
    );
  } else {
    CampsNearYouSport.init();
  }
})();
